import { t, useLanguage, formatMoney, translateError } from "../../i18n";
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicAPI, paymentAPI } from '../../services/api';
import { useCartStore, useOrderStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Minus, Plus, Trash2, CreditCard, SendHorizontal, Sparkles, TrendingUp, Tag, Check, X, Clock, Percent, AlertTriangle, Wallet } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PaymentMethodsInfo } from '../../components/payments/ExpressCheckout';
import { trackJourney } from '../../lib/journey';
export default function Cart() {
  useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const [reload, setReload] = useState(0);
  const [suggestionOptions, setSuggestionOptions] = useState({});
  const [cartNotice, setCartNotice] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [checkoutIntent, setCheckoutIntent] = useState(null);
  const [activeHappyHours, setActiveHappyHours] = useState([]);
  const [menuProducts, setMenuProducts] = useState([]);
  const [acceptsCash, setAcceptsCash] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [cashConfirmOpen, setCashConfirmOpen] = useState(false);
  const [availability, setAvailability] = useState({
    enabled: false,
    is_open: true,
    message: ''
  });
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    tableId,
    establishmentId
  } = useCartStore();
  const setActiveOrder = useOrderStore(s => s.setActiveOrder);
  const isClosed = availability.enabled && !availability.is_open;
  useEffect(() => {
    trackJourney('cart_viewed', tableId);
  }, [tableId]);
  const roundPrice = value => Math.round((value + Number.EPSILON) * 100) / 100;
  const getHappyHourDiscountPercent = categoryId => {
    if (!categoryId) return 0;
    let maxDiscount = 0;
    for (const hh of activeHappyHours) {
      if ((hh.category_ids || []).includes(categoryId)) {
        maxDiscount = Math.max(maxDiscount, hh.discount_percent || 0);
      }
    }
    return maxDiscount;
  };
  const getCartItemPricing = item => {
    const product = menuProducts.find(p => p.id === item.product_id);
    const baseProductPrice = product?.price ?? item.base_price ?? roundPrice((item.price || 0) - (item.options_cost || 0));
    const optionsCost = (item.selected_options || []).reduce((sum, optName) => {
      const opt = (product?.options || []).find(o => o.name === optName);
      return sum + (opt?.price || 0);
    }, 0);
    const categoryId = product?.category_id ?? item.category_id;
    const discountPercent = getHappyHourDiscountPercent(categoryId);
    const originalUnitPrice = roundPrice(baseProductPrice + optionsCost);
    let finalUnitPrice = originalUnitPrice;
    let happyHourApplied = false;
    if (discountPercent > 0) {
      const discountAmount = baseProductPrice * discountPercent / 100;
      finalUnitPrice = roundPrice(baseProductPrice - discountAmount + optionsCost);
      happyHourApplied = true;
    }
    return {
      originalUnitPrice,
      finalUnitPrice,
      happyHourApplied,
      discountPercent
    };
  };
  const getHappyHourCategoryNames = happyHour => {
    const categoryIds = happyHour?.category_ids || [];
    if (!categoryIds.length) return [];
    const names = categoryIds.map(categoryId => {
      const category = (menuProducts || []).find(p => p.category_id === categoryId);
      return category?.category_name || null;
    }).filter(Boolean);
    return [...new Set(names)];
  };
  const subtotal = items.reduce((sum, item) => {
    const pricing = getCartItemPricing(item);
    return sum + pricing.finalUnitPrice * item.quantity;
  }, 0);
  useEffect(() => {
    const loadData = async () => {
      if (!establishmentId) return;
      try {
        const [suggestionsRes, happyHoursRes, establishmentRes, menuRes, availabilityRes] = await Promise.all([publicAPI.getSuggestions(establishmentId).catch(() => ({
          data: []
        })), publicAPI.getActiveHappyHours(establishmentId), publicAPI.getEstablishment(establishmentId), publicAPI.getMenu(establishmentId), publicAPI.getAvailability(establishmentId)]);
        const cartProductIds = items.map(i => i.product_id);
        const filtered = (suggestionsRes.data || []).filter(p => !cartProductIds.includes(p.id));
        setSuggestions(filtered.slice(0, 4));
        setActiveHappyHours(happyHoursRes.data || []);
        setMenuProducts(menuRes.data?.products || []);
        setAcceptsCash(establishmentRes.data?.accepts_cash_payment !== false);
        setAvailability(availabilityRes.data || {
          enabled: false,
          is_open: true,
          message: ''
        });
      } catch (err) {
        console.error('Error loading data:', err);
        setSettingsLoaded(false);
        setCartNotice(t("Impossible de vérifier le panier. Réessayez avant de commander."));
        return;
      } finally {
        setLoadingSuggestions(false);
      }
      setSettingsLoaded(true);
    };
    loadData();
  }, [establishmentId, items, reload]);
  const handleAddSuggestion = product => {
    if (isClosed) {
      toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    addItem(product, suggestionOptions[product.id] || []);
    trackJourney('suggestion_added', tableId, {
      product_id: product.id
    });
    toast.success(t("{name} ajouté !", {
      name: product.name
    }));
    setSuggestions(prev => prev.filter(p => p.id !== product.id));
  };
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const response = await publicAPI.applyPromoCode(promoCode, subtotal, establishmentId);
      setPromoApplied(response.data);
      toast.success(t("Code promo appliqué !"));
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Code invalide');
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };
  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };
  const promoDiscount = promoApplied ? Math.min(subtotal, promoApplied.discount_type === 'percent' ? roundPrice(subtotal * promoApplied.discount_value / 100) : promoApplied.discount_value) : 0;
  const finalTotal = Math.max(subtotal - promoDiscount, 0);
  const isFreeOrder = finalTotal <= 0;
  const rememberOrderAndContinue = order => {
    const existingOrders = JSON.parse(localStorage.getItem('glassorder-active-orders') || '[]');
    const nextOrders = [{
      id: order.id,
      table_number: order.table_number,
      establishment_id: establishmentId,
      created_at: new Date().toISOString()
    }, ...existingOrders.filter(existingOrder => existingOrder.id !== order.id)];
    localStorage.setItem('glassorder-active-orders', JSON.stringify(nextOrders));
    setActiveOrder(order.id, order.status, order.table_number);
    clearCart();
    sessionStorage.removeItem('glassorder-checkout-attempt');
    navigate(`/order-confirmation?order_id=${order.id}`);
  };
  const handleConfirmCashPayment = async () => {
    setCashConfirmOpen(false);
    await handleSubmitOrder('cash');
  };
  const handleCheckoutClick = intent => {
    if (!settingsLoaded || loading) return;
    trackJourney('checkout_started', tableId);
    if (!tableId || items.length === 0 || isClosed) {
      if (!tableId) toast.error(t("Table non trouvée"));else if (items.length === 0) toast.error(t("Votre panier est vide"));else toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    if (intent === 'cash' && !acceptsCash && !isFreeOrder) {
      toast.error(t("Le paiement en espèces n'est pas disponible"));
      return;
    }
    if (!loadingSuggestions && suggestions.length > 0) {
      setCheckoutIntent(intent);
      setSuggestionsOpen(true);
      return;
    }
    if (intent === 'cash' && !isFreeOrder) setCashConfirmOpen(true);else handleSubmitOrder(intent);
  };
  const handleContinueAfterSuggestions = () => {
    const intent = checkoutIntent || (isFreeOrder ? 'free' : 'card');
    setSuggestionsOpen(false);
    setCheckoutIntent(null);
    if (intent === 'cash' && !isFreeOrder) setCashConfirmOpen(true);else handleSubmitOrder(intent);
  };
  const handleSubmitOrder = async (intent = 'card') => {
    if (submitting.current || !settingsLoaded) return;
    if (!tableId) {
      toast.error(t("Table non trouvée"));
      return;
    }
    if (items.length === 0) {
      toast.error(t("Votre panier est vide"));
      return;
    }
    if (isClosed) {
      toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    if (intent === 'cash' && !acceptsCash && !isFreeOrder) {
      toast.error(t("Le paiement en espèces n'est pas disponible"));
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      // Le serveur recalcule le total et convertit lui-même les montants nuls
      // en commandes gratuites. "card" évite d'exiger le paiement espèces.
      const paymentMethod = intent === 'cash' && !isFreeOrder ? 'cash' : 'card';
      const orderData = {
        table_id: tableId,
        payment_method: paymentMethod,
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          selected_options: i.selected_options || []
        })),
        promo_code: promoApplied?.code || null
      };
      orderData.expected_total = roundPrice(finalTotal);
      const signature = JSON.stringify(orderData);
      let attempt;
      try {
        attempt = JSON.parse(sessionStorage.getItem('glassorder-checkout-attempt') || 'null');
      } catch {}
      if (!attempt || attempt.signature !== signature) {
        attempt = {
          signature,
          request_id: crypto.randomUUID()
        };
        sessionStorage.setItem('glassorder-checkout-attempt', JSON.stringify(attempt));
      }
      orderData.request_id = attempt.request_id;
      const response = await publicAPI.createOrder(orderData);
      const order = response.data;
      const orderId = order.id;
      sessionStorage.setItem('glassorder-checkout-attempt', JSON.stringify({
        ...attempt,
        order_id: orderId
      }));
      if (order.status === 'cancelled') throw new Error(t("Cette commande a été annulée. Modifiez le panier pour recommencer."));
      if (order.status !== 'awaiting_payment' || order.payment_method === 'free' || Number(order.total_price) <= 0) {
        rememberOrderAndContinue(order);
        return;
      }
      if (intent !== 'cash') {
        setActiveOrder(orderId, order.status, order.table_number);
        localStorage.setItem('glassorder-pending-payment', orderId);
        const paymentResponse = await paymentAPI.createCheckout(orderId, window.location.origin);
        window.location.href = paymentResponse.data.url;
        return;
      }
      rememberOrderAndContinue(order);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || err.message || t("Erreur lors de la commande");
      if (err.response?.status === 409) {
        setCartNotice(message);
        setSettingsLoaded(false);
        setPromoApplied(null);
        setReload(value => value + 1);
        sessionStorage.removeItem('glassorder-checkout-attempt');
      }
      trackJourney('checkout_error', tableId, {
        status: err.response?.status || 'network'
      });
      toast.error(translateError(message, 'Erreur lors de la commande'));
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };
  if (items.length === 0) {
    return <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/menu')} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="cart-back-btn">

              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold" style={{
            fontFamily: 'Manrope'
          }}>{t("Panier")}

            </h1>
            <div className="w-9" />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-gray-500 mb-4">{t("Votre panier est vide")}</p>
            <Button onClick={() => navigate('/menu')} className="bg-[#111827] hover:bg-gray-800 rounded-full" data-testid="empty-cart-menu-btn">{t("Voir le menu")}

            </Button>
          </div>
        </div>
      </div>;
  }
  return <>
      <div className="min-h-screen bg-[#F9FAFB] pb-6 sm:pb-96">
        {cartNotice && <div role="alert" className="m-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{translateError(cartNotice)}<button className="ml-2 underline" onClick={() => setReload(v => v + 1)}>{t("Actualiser")}</button></div>}
        {localStorage.getItem('glassorder-pending-payment') && <div className="m-4 rounded-xl bg-violet-50 p-4"><button className="text-violet-700 underline" onClick={() => navigate(`/order-confirmation?order_id=${localStorage.getItem('glassorder-pending-payment')}`)}>{t("Reprendre ma commande en attente de paiement")}</button></div>}
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <button type="button" aria-label={t("Retour au menu")} onClick={() => navigate('/menu')} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="cart-back-btn">

              <ArrowLeft className="w-5 h-5" />
            </button>

            <h1 className="text-lg font-bold" style={{
            fontFamily: 'Manrope'
          }}>{t("Panier")}

            </h1>

            <button type="button" aria-label={t("Vider le panier")} onClick={clearCart} className="p-2 hover:bg-red-50 rounded-full transition-colors text-red-500" data-testid="clear-cart-btn">

              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {isClosed && <div className="px-4 pt-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-amber-100 p-2">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold">{t("Commande momentanément indisponible")}</p>
                  <p className="mt-1 text-sm leading-6">
                    {t(availability.message || '') || t("L'établissement est actuellement fermé.")}
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {activeHappyHours.length > 0 && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mx-4 mt-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-3 text-white">

            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <div>
                <p className="font-bold text-sm">{t("Happy Hour en cours !")}</p>
                <p className="text-xs opacity-90">
                  {activeHappyHours.map(hh => {
                const categoryNames = getHappyHourCategoryNames(hh);
                if (!categoryNames.length) {
                  return `${hh.name}: -${hh.discount_percent}%`;
                }
                return t("{name}: -{percent}% sur {categories}", {
                  name: hh.name,
                  percent: hh.discount_percent,
                  categories: categoryNames.join(", ")
                });
              }).join(' • ')}
                </p>
              </div>
            </div>
          </motion.div>}

        <div className="p-4 space-y-3">
          <AnimatePresence mode="popLayout">
            {items.map((item, index) => {
            const pricing = getCartItemPricing(item);
            return <motion.div key={`${item.product_id}-${item.optionKey || ''}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 20
            }} transition={{
              delay: index * 0.05
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>

                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                  </div>

                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="font-semibold text-gray-900 break-words" style={{
                    fontFamily: 'Manrope'
                  }}>
                        {item.name}
                      </h3>
                      {settingsLoaded && !menuProducts.some(p => p.id === item.product_id && p.available !== false) && <p className="text-sm text-red-700">{t("Indisponible — retirez cet article pour continuer.")}</p>}
                      <button className="text-xs underline text-slate-600" aria-label={t("Retirer {name}", {
                    name: item.name
                  })} onClick={() => removeItem(item.product_id, item.optionKey || '')}>{t("Retirer")}</button>

                      {item.selected_options?.length > 0 && <p className="text-xs text-purple-500">{item.selected_options.join(', ')}</p>}

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-sm text-gray-500" style={{
                      fontFamily: 'JetBrains Mono'
                    }}>
                          {pricing.finalUnitPrice === 0 ? t("Gratuit") : formatMoney(pricing.finalUnitPrice)}
                        </p>

                        {pricing.happyHourApplied && <>
                            <p className="text-xs text-gray-400 line-through" style={{
                        fontFamily: 'JetBrains Mono'
                      }}>
                              {formatMoney(pricing.originalUnitPrice)}
                            </p>
                            <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0">
                              -{pricing.discountPercent}%
                            </Badge>
                          </>}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                      <div className="flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.optionKey || '')} className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center active:scale-95 transition-transform" data-testid={`cart-decrease-${item.product_id}`}>

                          <Minus className="w-4 h-4" />
                        </button>

                        <span className="font-semibold min-w-[20px] text-center" style={{
                      fontFamily: 'JetBrains Mono'
                    }}>
                          {item.quantity}
                        </span>

                        <button onClick={() => {
                      if (isClosed) {
                        toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
                        return;
                      }
                      updateQuantity(item.product_id, item.quantity + 1, item.optionKey || '');
                    }} className={`w-8 h-8 rounded-full shadow flex items-center justify-center active:scale-95 transition-transform ${isClosed ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#7C3AED] text-white'}`} data-testid={`cart-increase-${item.product_id}`}>

                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <span className="font-bold text-[#111827]" style={{
                    fontFamily: 'JetBrains Mono'
                  }}>
                        {pricing.finalUnitPrice * item.quantity === 0 ? t("Gratuit") : formatMoney(pricing.finalUnitPrice * item.quantity)}
                      </span>
                    </div>
                  </div>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>

        <div className="px-4 pb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-purple-500" />
              <span className="font-semibold text-sm">{t("Code promo")}</span>
            </div>

            {promoApplied ? <div className="flex items-center justify-between bg-blue-50 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-violet-600" />
                  <div>
                    <span className="font-mono font-bold text-violet-700">{promoApplied.code}</span>
                    <p className="text-xs text-violet-600">
                      -{promoApplied.discount_type === 'percent' ? `${promoApplied.discount_value}%` : formatMoney(promoApplied.discount_value)}
                    </p>
                  </div>
                </div>

                <button onClick={handleRemovePromo} className="p-2 hover:bg-blue-100 rounded-full text-violet-600" data-testid="remove-promo-btn">

                  <X className="w-4 h-4" />
                </button>
              </div> : <div className="space-y-2">
                <div className="flex gap-2">
                  <Input placeholder={t("Entrez votre code")} value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} className="font-mono" data-testid="promo-code-input" />

                  <Button onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()} variant="outline" data-testid="apply-promo-btn">

                    {promoLoading ? '...' : t("Appliquer")}
                  </Button>
                </div>

                {promoError && <p className="text-xs text-red-500">{translateError(promoError, 'Code invalide')}</p>}
              </div>}
          </div>
        </div>

        <Dialog open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
          <DialogContent className="checkout-suggestions flex max-w-md flex-col gap-0 overflow-hidden rounded-3xl p-0">
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-5">
              <DialogHeader>
                <div className="flex items-center gap-2 pr-8">
                <div className="w-8 h-8 shrink-0 bg-amber-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0 text-left">
                  <DialogTitle className="break-words font-bold leading-snug text-gray-900" style={{
                    fontFamily: 'Manrope'
                  }}>{t("Complétez votre commande")}

                    </DialogTitle>
                  <DialogDescription className="text-left text-xs text-gray-500">{t("Nos clients adorent aussi")}</DialogDescription>
                </div>
              </div>
              </DialogHeader>

              <div className="mt-4 space-y-2">
                {suggestions.map(product => <motion.div key={product.id} initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="grid min-w-0 grid-cols-[3.5rem_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-xl bg-white p-3 shadow-sm" data-testid={`suggestion-${product.id}`}>

                    <div className="row-span-2 w-14 h-14 rounded-lg overflow-hidden bg-gray-100">
                      {product.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1">
                        <h4 className="min-w-0 break-words font-semibold text-sm">{product.name}</h4>
                        {product.is_bestseller && <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                            <TrendingUp className="w-2.5 h-2.5 mr-0.5" />{t("Top")}

                      </Badge>}
                      </div>
                      <p className="break-words text-xs text-gray-500">{product.description}</p>
                      {(product.options || []).map(opt => <label key={opt.name} className="mt-2 flex min-w-0 items-center gap-2 break-words text-sm">
                        <input type="checkbox" checked={(suggestionOptions[product.id] || []).includes(opt.name)} onChange={e => setSuggestionOptions(prev => ({
                      ...prev,
                      [product.id]: e.target.checked ? [...(prev[product.id] || []), opt.name] : (prev[product.id] || []).filter(n => n !== opt.name)
                    }))} />
                        {opt.name}{opt.price > 0 ? ` (+${formatMoney(opt.price)})` : ''}
                      </label>)}
                    </div>

                    <div className="col-start-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
                      <span className="font-bold text-sm whitespace-nowrap" style={{
                    fontFamily: 'JetBrains Mono'
                  }}>
                        {(() => {
                      const price = roundPrice(product.price * (1 - getHappyHourDiscountPercent(product.category_id) / 100) + (product.options || []).filter(o => (suggestionOptions[product.id] || []).includes(o.name)).reduce((sum, o) => sum + o.price, 0));
                      return price === 0 ? t("Gratuit") : formatMoney(price);
                    })()}
                      </span>
                      <button type="button" aria-label={t("Ajouter {name} au panier", {
                    name: product.name
                  })} disabled={isClosed} onClick={() => handleAddSuggestion(product)} className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-transform shadow-sm ${isClosed ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#7C3AED] text-white'}`} data-testid={`add-suggestion-${product.id}`}>

                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>
            </div>
            <DialogFooter className="min-w-0 shrink-0 border-t border-amber-100 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button className="h-auto min-h-12 w-full min-w-0 whitespace-normal break-words rounded-full px-4 py-3 text-center leading-snug" onClick={handleContinueAfterSuggestions}>
                {isFreeOrder ? t("Passer ma commande") : t("Continuer vers le paiement")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="cart-checkout relative sm:fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{t("Sous-total")}</span>
              <span style={{
              fontFamily: 'JetBrains Mono'
            }}>{subtotal === 0 ? t("Gratuit") : formatMoney(subtotal)}</span>
            </div>

            {promoApplied && <div className="flex items-center justify-between text-sm text-violet-600">
                <span className="flex items-center gap-1">
                  <Percent className="w-3 h-3" />{t("Réduction (")}
                {promoApplied.code})
                </span>
                <span style={{
              fontFamily: 'JetBrains Mono'
            }}>-{formatMoney(promoDiscount)}</span>
              </div>}

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="font-semibold">{t("Total")}</span>
              <span className="text-2xl font-bold text-[#111827]" style={{
              fontFamily: 'JetBrains Mono'
            }}>
                {isFreeOrder ? t("Gratuit") : formatMoney(finalTotal)}
              </span>
            </div>
          </div>

          {isFreeOrder ? <Button onClick={() => handleCheckoutClick('free')} disabled={loading || isClosed || !settingsLoaded} data-testid="submit-free-order-btn" className="w-full rounded-full py-6 text-lg font-semibold shadow-lg bg-[#7C3AED] text-white">

              <SendHorizontal className="w-5 h-5" />
              {loading ? t("Envoi...") : t("Passer la commande gratuitement")}
            </Button> : <>
          <Button onClick={() => handleCheckoutClick('cash')} disabled={loading || !settingsLoaded || !acceptsCash || isClosed} data-testid="submit-order-btn" className={`w-full rounded-full py-6 text-lg font-semibold shadow-lg flex items-center justify-center gap-3 active:scale-95 transition-transform ${loading || !settingsLoaded || !acceptsCash || isClosed ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#111827] hover:bg-gray-800 text-white'}`}>

            <SendHorizontal className="w-5 h-5" />
            {!settingsLoaded ? t("Vérification...") : loading ? t("Envoi...") : isClosed ? t("Établissement fermé") : t("Payer en espèces")}
          </Button>

          <Button onClick={() => handleCheckoutClick('card')} disabled={loading || isClosed || !settingsLoaded} variant="outline" data-testid="pay-order-btn" className={`w-full border-2 rounded-full py-6 text-lg font-semibold flex items-center justify-center gap-3 active:scale-95 transition-transform ${isClosed ? 'border-gray-300 text-gray-400 cursor-not-allowed' : 'border-[#7C3AED] text-[#7C3AED] hover:bg-blue-50'}`}>

            <CreditCard className="w-5 h-5" />
            {isClosed ? t("Paiement indisponible") : t("Payer maintenant")}
          </Button>

          </>}

          {!settingsLoaded ? <p className="text-center text-xs text-gray-400">{t("Vérification des moyens de paiement...")}

          </p> : isFreeOrder ? <p className="text-center text-sm text-violet-700">{t("Aucun paiement nécessaire")}</p> : isClosed ? <p className="text-center text-xs text-amber-600 font-medium">
              {t(availability.message || '') || t("L'établissement est actuellement fermé")}
            </p> : acceptsCash ? <p className="text-center text-xs text-gray-400">{t("Paiement optionnel - vous pouvez aussi payer au comptoir")}

          </p> : <p className="text-center text-xs text-amber-500 font-medium">{t("Paiement par carte obligatoire")}

          </p>}

          {!isFreeOrder && <div className="pt-1"><PaymentMethodsInfo /></div>}
        </div>
      </div>

      <Dialog open={cashConfirmOpen} onOpenChange={setCashConfirmOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100">
              <Wallet className="h-7 w-7 text-orange-600" />
            </div>

            <DialogTitle className="text-center text-xl" style={{
            fontFamily: 'Manrope'
          }}>{t("Confirmer le paiement en espèces ?")}

            </DialogTitle>

            <DialogDescription className="text-center leading-6">{t("Votre commande sera envoyée au bar et vous devrez régler le montant de")}
              {' '}
              <span className="font-semibold text-gray-900" style={{
              fontFamily: 'JetBrains Mono'
            }}>
                {formatMoney(finalTotal)}
              </span>{' '}{t("en espèces auprès du serveur ou au comptoir.")}

            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-3 text-sm text-orange-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{t("En confirmant, vous vous engagez à payer cette commande en espèces.")}

              </p>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={handleConfirmCashPayment} disabled={loading} className="w-full rounded-full bg-[#111827] py-6 text-base font-semibold hover:bg-gray-800" data-testid="confirm-cash-payment-btn">

              {loading ? t("Envoi...") : t("Confirmer le paiement en espèces")}
            </Button>

            <Button onClick={() => setCashConfirmOpen(false)} disabled={loading} variant="outline" className="w-full rounded-full py-6 text-base font-semibold" data-testid="cancel-cash-payment-btn">{t("Annuler")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}
